import {
  articleSchema,
  breadcrumbSchema,
  pageUrl,
  siteSchema,
  webPageSchema,
  type PageHead,
  type SiteIdentity,
} from "@generalapplications/html-render";

import { isPublicBlogPost, type BlogPostSummary } from "@/content/blog";
import { site } from "@/content/site";

/** The site, as its structured data names it. */
export const siteIdentity: SiteIdentity = {
  url: site.baseUrl,
  name: site.name,
  description: site.description,
  logoPath: site.logoPath,
};

/** One step of a page's trail, for its BreadcrumbList. */
export type TrailStep = { name: string; path: string };

export type PageHeadInput = {
  /** The page's own path, as its route writes it. */
  path: string;
  title: string;
  description: string;
  /** Its share picture, a path on the site; the site's default otherwise. */
  imagePath?: string;
  /** Not for search: no canonical, no WebPage block. */
  noIndex?: boolean;
  /** The trail from home to this page, itself last. */
  breadcrumbs?: readonly TrailStep[];
  /** The post a blog page is; described as a BlogPosting once public. */
  article?: BlogPostSummary;
};

/**
 * Everything a page says about itself to a search engine and a link preview,
 * built once from what the page knows. The page renders it with `PageHead`;
 * the browser applies it and the prerender bakes it in.
 */
export function pageHead({
  path,
  title,
  description,
  imagePath = site.ogImagePath,
  noIndex = false,
  breadcrumbs,
  article,
}: PageHeadInput): PageHead {
  const url = pageUrl(site.baseUrl, path.toLowerCase());
  const schema = (id: string, data: object) => ({ id, data });

  return {
    siteName: site.name,
    title,
    description,
    robots: noIndex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large",
    ...(!noIndex && { canonical: url }),
    url,
    type: "website",
    image: { url: `${site.baseUrl}${imagePath}`, alt: title },
    feeds: [{ title: `${site.name} Blog`, href: `${site.baseUrl}/feed.xml` }],
    // The site graph first: the other blocks point at its @ids.
    schemas: [
      schema("organization-structured-data", siteSchema(siteIdentity)),
      ...(noIndex
        ? []
        : [
            schema(
              "webpage-structured-data",
              webPageSchema(siteIdentity, {
                path: path.toLowerCase(),
                title,
                description,
                imagePath,
              }),
            ),
          ]),
      ...(breadcrumbs?.length
        ? [
            schema(
              "breadcrumb-structured-data",
              breadcrumbSchema(siteIdentity, [...breadcrumbs]),
            ),
          ]
        : []),
      ...(article && isPublicBlogPost(article)
        ? [
            schema(
              "blogposting-structured-data",
              articleSchema(siteIdentity, {
                path,
                title: article.title,
                description: article.seoDescription,
                published: article.publishedAt,
                modified: article.updatedAt || undefined,
                imagePath: article.image,
                author: {
                  type: "Organization",
                  name: site.name,
                  url: `${site.baseUrl}/about`,
                },
              }),
            ),
          ]
        : []),
    ],
  };
}

/** The trail from home to a page under a section. */
export function sectionTrail(
  section: TrailStep,
  page: TrailStep,
): readonly TrailStep[] {
  return [{ name: "Home", path: "/" }, section, page];
}
